import { displayText } from "../../locales/display";
import { msg } from "../../locales/translate";
// Pure helpers for the Providers section: statuses, labels, dates, search and per-entity summaries.
import { formatDisplayDate, parseServerDate } from "../../utils/dateTime.ts";

export type TTone = "positive" | "neutral" | "info" | "warning" | "negative";

type TChannelLike = {
	id: number;
	name?: string;
	status?: string;
	currency?: string;
	terminal?: { id: number; name?: string; type?: string | null } | null;
	provider?: { id: number; name?: string } | null;
	integration?: { id: number; name?: string } | null;
	linked?: { status?: string } | null;
};

export const humanize = (key: string) =>
	key
		.replace(/[_-]+/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/^\w/, char => char.toUpperCase());

export const statusTone = (status?: string | null): TTone => {
	const value = (status ?? "").toLowerCase();
	if (value === "active" || value === "enabled") return "positive";
	if (value === "deleted" || value === "blocked") return "negative";
	return "neutral";
};

export const statusLabel = (status?: string | null) => displayText(status ? humanize(status) : "Unknown");

const METHOD_TYPES: Record<string, string> = {
	get payment_card() { return msg("paymentCard"); },
	apple_pay: "Apple Pay",
	google_pay: "Google Pay",
	get open_banking() { return msg("openBanking_6303c270"); },
	get oct() { return msg("octPayout"); },
};

export const methodTypeLabel = (type?: string | null) => (type ? (METHOD_TYPES[type] ?? humanize(type)) : msg("typeNotSet"));

const CARD_BRANDS: Record<string, string[]> = { visa: ["visa"], mastercard: ["mastercard"], all: ["visa", "mastercard"] };

/** Card schemes a channel accepts; an empty list means the channel does not restrict them. */
export const cardBrands = (instrument?: string | null) => CARD_BRANDS[instrument ?? ""] ?? [];

/** A merchant link can only narrow the channel's own status, and a deleted link always wins. */
export const channelStatus = (channel: Pick<TChannelLike, "status" | "linked">) => {
	let status = channel.status ?? "";
	if (channel.linked) {
		if (status === "active") status = channel.linked.status ?? status;
		if (channel.linked.status === "deleted") status = "deleted";
	}
	return status;
};

/** Server timestamps arrive as seconds, milliseconds, numeric strings or date strings. */
export const toDate = (value: unknown): Date | null => {
	if (value === null || value === undefined || value === "" || value === 0) return null;
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
	if (typeof value === "number") return Number.isFinite(value) && value > 0 ? new Date(value < 1e11 ? value * 1000 : value) : null;
	if (typeof value !== "string") return null;
	if (/^\d+$/.test(value.trim())) return toDate(Number(value));
	return parseServerDate(value);
};

/** Presentation uses the shared zone; feeStates below always compares UTC days. */
export const formatDate = (value: unknown) => {
	const date = toDate(value);
	return date ? formatDisplayDate(typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : date, { locale: "en-US", format: { month: "short", day: "2-digit", year: "numeric" } }) : null;
};

const dayKey = (date: Date) => date.toISOString().slice(0, 10);

export type TFeeState = "current" | "scheduled" | "archived";

/**
 * Which configuration applies today: the latest one that has started is current, later ones are scheduled
 * and the rest are archived. The server's own `historical` flag also marks a running configuration as soon as a
 * newer one is scheduled, so it is not used for the label.
 */
export const feeStates = (fees: { activated_from?: unknown }[], now = new Date()): TFeeState[] => {
	const today = dayKey(now);
	const days = fees.map(fee => {
		const date = toDate(fee.activated_from);
		return date ? dayKey(date) : "";
	});
	const current = days.reduce((best, day, index) => (day && day <= today && (best < 0 || day > days[best]) ? index : best), -1);
	return days.map((day, index) => (day > today ? "scheduled" : index === current ? "current" : "archived"));
};

export const normalizeText = (value: unknown) => String(value ?? "").toLowerCase().trim();

/** Every word of the query must appear in one of the values. */
export const matchesQuery = (query: string, values: unknown[]) => {
	const terms = normalizeText(query).split(/\s+/).filter(Boolean);
	if (!terms.length) return true;
	const haystack = values.map(normalizeText).join(" ");
	return terms.every(term => haystack.includes(term));
};

export const compareText = (a: unknown, b: unknown) => String(a ?? "").localeCompare(String(b ?? ""), undefined, { sensitivity: "base", numeric: true });

export const uniqueSorted = (values: (string | null | undefined)[]) => [...new Set(values.filter((value): value is string => !!value))].sort(compareText);

export const isSecretKey = (key: string) => /(secret|password|passwd|token|private|api_?key|signature)/i.test(key);

export const formatIban = (iban?: string | null) =>
	(iban ?? "")
		.replace(/\s+/g, "")
		.replace(/(.{4})/g, "$1 ")
		.trim();

export type TChannelsSummary = {
	total: number;
	active: number;
	withoutIntegration: number;
	currencies: string[];
	methods: { id: number; name: string; type?: string | null }[];
};

/** Deleted channels are kept out of the counts, so the numbers describe what can process payments. */
export const summarizeChannels = (channels: TChannelLike[]): TChannelsSummary => {
	const live = channels.filter(channel => channelStatus(channel) !== "deleted");
	const methods = new Map<number, { id: number; name: string; type?: string | null }>();
	for (const channel of live) {
		if (channel.terminal?.id && !methods.has(channel.terminal.id))
			methods.set(channel.terminal.id, { id: channel.terminal.id, name: channel.terminal.name ?? "Method", type: channel.terminal.type });
	}
	return {
		total: live.length,
		active: live.filter(channel => channelStatus(channel) === "active").length,
		withoutIntegration: live.filter(channel => !channel.integration).length,
		currencies: uniqueSorted(live.map(channel => channel.currency)),
		methods: [...methods.values()].sort((a, b) => compareText(a.name, b.name)),
	};
};

export const groupBy = <T, K>(items: T[], key: (item: T) => K) => {
	const groups = new Map<K, T[]>();
	for (const item of items) {
		const id = key(item);
		const group = groups.get(id);
		if (group) group.push(item);
		else groups.set(id, [item]);
	}
	return groups;
};

/** The server caps channel lists at this many rows. */
export const CHANNELS_LIMIT = 100;

export const plural = (count: number, one: string, many = one + "s") => `${count} ${count === 1 ? one : many}`;
